import { useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Calendar, LogOut, Truck, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { confirmSignOutWithUnsyncedWork } from "@/lib/unsyncedWorkGuard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { useUserRole } from "@/hooks/useUserRole";

interface NavItem {
  label: string;
  icon: React.ElementType;
  path?: string;
  matchPaths?: string[];
  action?: () => void;
}

export const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { markManualLogout } = useAuth();
  const { isAdmin, isDriver } = useUserRole();

  // Hide bottom nav on native app - we use native navigation patterns instead
  // Check at render time to ensure Capacitor bridge is initialized
  const isNativePlatform = Capacitor.isNativePlatform();
  if (isNativePlatform) {
    return null;
  }

  const handleLogout = async () => {
    try {
      if (!(await confirmSignOutWithUnsyncedWork())) return;
      markManualLogout();
      await supabase.auth.signOut();
      toast.success("Logged out successfully");
      navigate("/auth");
    } catch (error) {
      toast.error("Error logging out");
    }
  };

  // F-08: drivers get Dashboard / Jobs / Schedule / More (Log Out lives in More).
  const driverNavItems: NavItem[] = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard", matchPaths: ["/dashboard"] },
    { label: "Jobs", icon: Truck, path: "/jobs", matchPaths: ["/jobs", "/pending-jobs", "/previous-jobs", "/resume-job", "/job/"] },
    { label: "Schedule", icon: Calendar, path: "/schedule", matchPaths: ["/schedule"] },
    { label: "More", icon: Menu, path: "/more", matchPaths: ["/more"] },
  ];

  // Define nav items - filter invoicing for drivers
  const allNavItems: NavItem[] = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/dashboard",
      matchPaths: ["/dashboard"],
    },
    ...(isAdmin ? [{
      label: "Invoices",
      icon: FileText,
      path: "/invoicing",
      matchPaths: ["/invoicing", "/ready-to-invoice", "/invoice"],
    }] : []),
    {
      label: "Schedule",
      icon: Calendar,
      path: "/schedule",
      matchPaths: ["/schedule"],
    },
    {
      label: "Log Out",
      icon: LogOut,
      action: handleLogout,
    },
  ];

  const navItems = isDriver ? driverNavItems : allNavItems;

  const isActive = (item: NavItem) => {
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname.startsWith(p));
    }
    return item.path ? location.pathname === item.path : false;
  };

  const handleClick = (item: NavItem) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <nav aria-label="Primary navigation" className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border sm:hidden safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          
          return (
            <button
              key={item.label}
              onClick={() => handleClick(item)}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
                active 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className={cn("h-5 w-5", active && "text-primary")} />
              <span className={cn(
                "text-[10px] font-medium",
                active && "text-primary"
              )}>
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
